import { useEffect, useRef } from "react";
import { toast } from "sonner";

import { useRenameConversation } from "~/ai-chat/data-source/use-conversations";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";

/**
 * Rename a persisted conversation with optimistic feedback: the conversations
 * list updates immediately (rolled back by the mutation on error), and when the
 * renamed conversation is the one open in the chat header, the header title
 * follows the same optimistic/rollback cycle.
 */
export function useRenameChat() {
  const { activeConversationId, chatTitle, setChatTitle } = useAiChat();
  const { mutate, isPending } = useRenameConversation();

  // The failure rollback needs the header state at error time, not at call time:
  // by then the user may have switched conversations or issued a newer rename,
  // and restoring this operation's stale title would clobber the current one.
  const headerStateRef = useRef({ activeConversationId, chatTitle });
  useEffect(() => {
    headerStateRef.current = { activeConversationId, chatTitle };
  });

  const rename = (conversationId: string, title: string | null) => {
    const isActiveConversation = conversationId === activeConversationId;
    const previousTitle = chatTitle;
    if (isActiveConversation) setChatTitle(title);
    mutate(
      { conversationId, title },
      {
        onError: () => {
          const header = headerStateRef.current;
          const ownsHeaderTitle =
            isActiveConversation && header.activeConversationId === conversationId && header.chatTitle === title;
          if (ownsHeaderTitle) setChatTitle(previousTitle);
          toast.error("Couldn't rename the chat. Please try again.");
        },
      },
    );
  };

  return { rename, isRenaming: isPending };
}
